import React, { useState } from 'react';
import { motion } from 'framer-motion';
import apiService from '../../services/api';
import { ShutdownResponse } from '../../types/bot';

interface ShutdownModalProps {
  isOpen: boolean;
  onClose: () => void;
  onShutdownComplete: () => void;
}

type ShutdownPhase = 'confirm' | 'in_progress' | 'complete' | 'error';

const ShutdownModal: React.FC<ShutdownModalProps> = ({
  isOpen,
  onClose,
  onShutdownComplete,
}) => {
  const [phase, setPhase] = useState<ShutdownPhase>('confirm');
  const [password, setPassword] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [result, setResult] = useState<ShutdownResponse | null>(null);
  const [error, setError] = useState<string>('');
  const [currentStep, setCurrentStep] = useState<string>('');

  const isConfirmValid = password.length > 0 && confirmed;

  const handleShutdown = async () => {
    setPhase('in_progress');
    setError('');
    setCurrentStep('Closing Orca Whirlpool positions...');

    try {
      // Simulate progress updates (actual operation is synchronous)
      const progressTimer = setTimeout(() => {
        setCurrentStep('Closing Drift Protocol positions...');
      }, 5000);

      const withdrawTimer = setTimeout(() => {
        setCurrentStep('Withdrawing funds from Drift...');
      }, 10000);

      const response = await apiService.bot.shutdown(password);

      clearTimeout(progressTimer);
      clearTimeout(withdrawTimer);

      setResult(response);
      setPhase(response.success ? 'complete' : 'error');

      if (response.success) {
        onShutdownComplete();
      }
    } catch (err: any) {
      setError(
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.message ||
        'Failed to execute shutdown'
      );
      setPhase('error');
    }
  };

  const handleClose = () => {
    // Reset state when closing
    setPhase('confirm');
    setPassword('');
    setConfirmed(false);
    setResult(null);
    setError('');
    setCurrentStep('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-gray-900 rounded-2xl p-6 max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto"
      >
        {phase === 'confirm' && (
          <>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-red-500/20 rounded-xl flex items-center justify-center">
                <svg
                  className="w-6 h-6 text-red-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">Emergency Exit</h3>
                <p className="text-gray-400 text-sm">Complete System Shutdown</p>
              </div>
            </div>

            <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-4">
              <p className="text-red-400 font-semibold mb-2">
                This action is IRREVERSIBLE
              </p>
              <p className="text-gray-300 text-sm mb-3">
                This will immediately:
              </p>
              <ul className="text-sm text-gray-400 space-y-1">
                <li className="flex items-start gap-2">
                  <span className="text-red-400">1.</span>
                  <span>Close ALL Orca Whirlpool liquidity positions</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-400">2.</span>
                  <span>Close ALL Drift Protocol hedge positions</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-400">3.</span>
                  <span>Withdraw ALL funds from Drift to your wallet</span>
                </li>
              </ul>
            </div>

            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 mb-4">
              <p className="text-yellow-400 text-sm">
                <strong>Warning:</strong> Closing positions at current market prices may result
                in slippage losses. This operation may take several minutes.
              </p>
            </div>

            <div className="mb-4">
              <label className="block text-sm text-gray-400 mb-2">
                Enter your <span className="text-white font-semibold">password</span> to confirm:
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                className={`w-full px-4 py-3 bg-black border rounded-xl
                          text-white focus:outline-none transition-colors
                          ${
                            password.length > 0
                              ? 'border-green-500'
                              : 'border-gray-700 focus:border-gray-500'
                          }`}
              />
            </div>

            <label className="flex items-start gap-3 mb-6 cursor-pointer">
              <input
                type="checkbox"
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
                className="mt-1 w-4 h-4 rounded border-gray-600 bg-black
                         text-red-500 focus:ring-red-500 focus:ring-offset-0"
              />
              <span className="text-sm text-gray-300">
                I understand this will close all my positions and cannot be undone
              </span>
            </label>

            <div className="flex gap-3">
              <motion.button
                whileTap={{ scale: 0.98 }}
                onClick={handleClose}
                className="flex-1 py-3 bg-gray-800 hover:bg-gray-700
                         text-gray-300 font-semibold rounded-xl transition-colors"
              >
                Cancel
              </motion.button>
              <motion.button
                whileTap={{ scale: 0.98 }}
                onClick={handleShutdown}
                disabled={!isConfirmValid}
                className="flex-1 py-3 bg-red-500 hover:bg-red-600
                         text-white font-bold rounded-xl transition-colors
                         disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Confirm Shutdown
              </motion.button>
            </div>
          </>
        )}

        {phase === 'in_progress' && (
          <div className="text-center py-8">
            <motion.div
              className="w-16 h-16 mx-auto mb-6 border-4 border-gray-600 border-t-red-500 rounded-full"
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
            />
            <h3 className="text-xl font-bold text-white mb-2">
              Shutdown in Progress
            </h3>
            <p className="text-gray-400 mb-4">
              Please do not close this window
            </p>
            <div className="bg-gray-800 rounded-xl p-4">
              <p className="text-yellow-400 text-sm font-medium">{currentStep}</p>
            </div>
          </div>
        )}

        {phase === 'complete' && result && (
          <>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center">
                <svg
                  className="w-6 h-6 text-green-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">Shutdown Complete</h3>
                <p className="text-gray-400 text-sm">{result.message}</p>
              </div>
            </div>

            <div className="space-y-3 mb-6">
              <div className="bg-gray-800 rounded-xl p-4">
                <h4 className="text-sm font-semibold text-gray-400 mb-2">
                  Orca Whirlpool Positions
                </h4>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-300">Positions Closed</span>
                  <span className="text-green-400 font-semibold">
                    {result.orca_positions_closed}
                  </span>
                </div>
                {result.orca_positions_failed > 0 && (
                  <div className="flex justify-between text-sm mt-1">
                    <span className="text-gray-300">Failed to Close</span>
                    <span className="text-red-400 font-semibold">
                      {result.orca_positions_failed}
                    </span>
                  </div>
                )}
              </div>

              <div className="bg-gray-800 rounded-xl p-4">
                <h4 className="text-sm font-semibold text-gray-400 mb-2">
                  Drift Protocol
                </h4>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-300">Positions Closed</span>
                  <span className="text-green-400 font-semibold">
                    {result.drift_positions_closed}
                  </span>
                </div>
                {result.drift_positions_failed > 0 && (
                  <div className="flex justify-between text-sm mt-1">
                    <span className="text-gray-300">Failed to Close</span>
                    <span className="text-red-400 font-semibold">
                      {result.drift_positions_failed}
                    </span>
                  </div>
                )}
                <div className="flex justify-between text-sm mt-1">
                  <span className="text-gray-300">Funds Withdrawn</span>
                  <span className="text-green-400 font-semibold">
                    ${result.drift_amount_withdrawn.toFixed(2)}
                  </span>
                </div>
              </div>

              {result.errors && result.errors.length > 0 && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
                  <h4 className="text-sm font-semibold text-red-400 mb-2">Errors</h4>
                  <ul className="text-sm text-gray-300 space-y-1">
                    {result.errors.map((err, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <span className="text-red-400">-</span>
                        <span>{err}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <motion.button
              whileTap={{ scale: 0.98 }}
              onClick={handleClose}
              className="w-full py-3 bg-white hover:bg-gray-100
                       text-black font-bold rounded-xl transition-colors"
            >
              Close
            </motion.button>
          </>
        )}

        {phase === 'error' && (
          <>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-red-500/20 rounded-xl flex items-center justify-center">
                <svg
                  className="w-6 h-6 text-red-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">Shutdown Failed</h3>
                <p className="text-gray-400 text-sm">An error occurred during shutdown</p>
              </div>
            </div>

            <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-6">
              <p className="text-red-400 text-sm">{error || result?.error || 'Unknown error occurred'}</p>
            </div>

            {result && (result.orca_positions_closed > 0 || result.drift_positions_closed > 0) && (
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 mb-6">
                <p className="text-yellow-400 text-sm font-medium mb-2">Partial Results:</p>
                <p className="text-gray-300 text-sm">
                  Orca: {result.orca_positions_closed} closed, {result.orca_positions_failed} failed
                </p>
                <p className="text-gray-300 text-sm">
                  Drift: {result.drift_positions_closed} closed, ${result.drift_amount_withdrawn.toFixed(2)} withdrawn
                </p>
              </div>
            )}

            <div className="flex gap-3">
              <motion.button
                whileTap={{ scale: 0.98 }}
                onClick={handleClose}
                className="flex-1 py-3 bg-gray-800 hover:bg-gray-700
                         text-gray-300 font-semibold rounded-xl transition-colors"
              >
                Close
              </motion.button>
              <motion.button
                whileTap={{ scale: 0.98 }}
                onClick={() => {
                  setPhase('confirm');
                  setPassword('');
                  setConfirmed(false);
                  setError('');
                }}
                className="flex-1 py-3 bg-red-500 hover:bg-red-600
                         text-white font-bold rounded-xl transition-colors"
              >
                Try Again
              </motion.button>
            </div>
          </>
        )}
      </motion.div>
    </div>
  );
};

export default ShutdownModal;
